import { Button, Form, Input, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
    updateBiddingModalData,
    updateBiddingModalDisplay,
} from "@/features/modalSlice";
import { useState } from "react";
import useGetModulesData from "@/hooks/useGetModulesData";
import { showToast } from "@/ts/modals";
import { updateCampaigns } from "@/ts/utils";
import { setUpdateGridData } from "@/ts/filters";
import { BiddingType, BiddingTypeConstants } from "@/ts/constants";
import { ProgramTypeText, SubModule } from "@/ts/common-types";

interface IBiddingModal {
    biddingModal: {
        value: {
            display: boolean;
            data: Record<string, string | number>;
        };
    };
}

const BiddingModal: React.FC = () => {
    const { display: biddingModalDisplay, data: biddingModalData } =
        useSelector((state: IBiddingModal) => state.biddingModal.value);
    const [cpm, setCpm] = useState<number>(biddingModalData.cpm_bid as number);
    const [inputValue, setInputValue] = useState<Record<string, number>>({
        auto: (biddingModalData.auto_bid as number) || 0,
        contextual_multiplier:
            biddingModalData.contextual_multiplier_bid as number,
        premium_placements: biddingModalData.premium_placements_bid as number,
        product_listing_page: biddingModalData.product_page_bid as number,
        home_page: biddingModalData.home_page_bid as number,
        top_of_search: biddingModalData.top_of_search_bid as number,
        premium_users: biddingModalData.premium_placements_bid as number,
    });
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const componentConfig = useGetModulesData({
        type: "component-config",
        activeModuleName: SubModule.CAMPAIGN,
    });
    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.CAMPAIGN,
    });

    const handleClose = () => {
        dispatch(updateBiddingModalDisplay(false));
        dispatch(updateBiddingModalData(null));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const response = await updateCampaigns([
                {
                    state: biddingModalData.state as string,
                    attribute: [
                        BiddingType.contextual_multiplier,
                        BiddingType.premium_placements,
                        BiddingType.product_listing_page,
                        BiddingType.home_page,
                        BiddingType.top_of_search,
                        BiddingType.premium_users,
                        BiddingType.auto,
                        "cpm",
                    ],
                    campaignId: biddingModalData.sys_campaign_id.toString(),
                    ...Object.entries(inputValue).reduce(
                        (acc, [key, value]) => {
                            if (key in BiddingType) {
                                acc[
                                    BiddingType[key as keyof typeof BiddingType]
                                ] = value;
                            }
                            return acc;
                        },
                        {} as Record<string, any>
                    ),
                    cpm: cpm,
                },
            ]);

            if (!response.status) {
                throw new Error(response.message);
            }

            setUpdateGridData({
                params: {
                    data: biddingModalData,
                },
                value: inputValue.contextual_multiplier,
                isReduxState: true,
                additionalValuesToUpdate: {
                    contextual_multiplier_bid: inputValue.contextual_multiplier,
                    premium_placements_bid: componentConfig[
                        BiddingTypeConstants.PREMIUM_PLACEMENTS
                    ].disabledTypes.includes(biddingModalData.program_type)
                        ? inputValue.premium_users
                        : inputValue.premium_placements,
                    product_page_bid: inputValue.product_listing_page,
                    home_page_bid: inputValue.home_page,
                    top_of_search_bid: inputValue.top_of_search,
                    auto_bid: inputValue.auto,
                    cpm: cpm,
                },
                modalParamToChange: "contextual_multiplier_bid",
            });

            showToast("success", response.message);
            dispatch(updateBiddingModalDisplay(false));
            dispatch(updateBiddingModalData(null));
        } catch (err: any) {
            const resErrMsg = (err as Error).message;
            const errorMessage =
                ` Error While Updating Bidding Strategy for ${biddingModalData.campaign_name}` +
                `${resErrMsg && ": " + resErrMsg}`;
            showToast("danger", errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            centered
            open={biddingModalDisplay}
            title="Bidding"
            onOk={handleClose}
            width={600}
            onCancel={handleClose}
            styles={{
                body: {
                    maxHeight: "26vh",
                    overflowY: "auto",
                },
            }}
            footer={[
                <Button
                    type="primary"
                    loading={loading}
                    key="submit"
                    onClick={handleSubmit}
                >
                    Submit
                </Button>,
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
            ]}
        >
            <div>
                <Form
                    name="basic"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    style={{ maxWidth: 600, paddingTop: "1.5rem" }}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                >
                    {gridParams.isCpmChangeAllowed &&
                        gridParams.cpmChangeAllowedTypes.includes(
                            biddingModalData.program_type
                        ) && (
                            <Form.Item name="cpm" label="CPM" key="cpm">
                                <Input
                                    className="border ms-3"
                                    type="number"
                                    defaultValue={cpm}
                                    onChange={(e) => {
                                        setCpm(+e.target.value || 0);
                                    }}
                                    style={{ width: "100px" }}
                                />
                            </Form.Item>
                        )}

                    {Object.keys(inputValue).map((key, index) => {
                        if (
                            componentConfig[key].disabledTypes.includes(
                                biddingModalData.targetingtype
                            ) ||
                            componentConfig[key].disabledTypes.includes(
                                biddingModalData.program_type
                            )
                        ) {
                            return null;
                        } else {
                            return (
                                <Form.Item
                                    name={key}
                                    label={componentConfig[key].label}
                                    key={index}
                                >
                                    <Input
                                        className="border ms-3"
                                        type="number"
                                        defaultValue={inputValue[key]}
                                        suffix={
                                            key ===
                                            ProgramTypeText.AUTO.toLowerCase()
                                                ? ""
                                                : "%"
                                        }
                                        onChange={(e) => {
                                            setInputValue((prev) => {
                                                return {
                                                    ...prev,
                                                    [key]: +e.target.value || 0,
                                                };
                                            });
                                        }}
                                        style={{ width: "100px" }}
                                    />
                                </Form.Item>
                            );
                        }
                    })}
                </Form>
            </div>
        </Modal>
    );
};

export default BiddingModal;
